(function () {
  'use strict';

  if (window.UnifiedTracking) {
    return;
  }

  const VERSION = '2.1.0';

  let eventSequence = 0;

  /*
   * ------------------------------------------------------------
   * HELPERS
   * ------------------------------------------------------------
   */

  function getConfig() {
    return (
      window.UnifiedTrackingConfig ||
      {}
    );
  }

  function isDebugEnabled() {
    return (
      getConfig().debug === true
    );
  }

  function isPlainObject(value) {
    return Boolean(
      value &&
      typeof value === 'object' &&
      !Array.isArray(value)
    );
  }

  function nowIso() {
    return new Date()
      .toISOString();
  }

  function nextEventId() {
    eventSequence += 1;

    return (
      'ute_' +
      Date.now()
        .toString(36) +
      '_' +
      eventSequence
        .toString(36)
    );
  }

  /*
   * ------------------------------------------------------------
   * MODULE ACCESS
   * ------------------------------------------------------------
   */

  function getContext() {
    return (
      window.UnifiedContext ||
      null
    );
  }

  function getNormalizer() {
    return (
      window.UnifiedNormalizer ||
      null
    );
  }

  function getValidator() {
    return (
      window.UnifiedValidator ||
      null
    );
  }

  function getRulesEngine() {
    return (
      window.UnifiedRulesEngine ||
      null
    );
  }

  function getConsent() {
    return (
      window.UnifiedConsent ||
      null
    );
  }

  function getProviderManager() {
    return (
      window.UnifiedProviderManager ||
      null
    );
  }

  function getJourney() {
    return (
      window.UnifiedJourney ||
      null
    );
  }

  /*
   * ------------------------------------------------------------
   * PERFORMANCE
   * ------------------------------------------------------------
   */

  function performanceStart(
    label,
    meta
  ) {
    if (
      window.UnifiedPerformance &&
      typeof window.UnifiedPerformance.start ===
        'function'
    ) {
      return window.UnifiedPerformance.start(
        label,
        meta || {}
      );
    }

    return null;
  }

  function performanceEnd(
    tokenId,
    meta
  ) {
    if (
      tokenId === null ||
      typeof tokenId ===
        'undefined'
    ) {
      return null;
    }

    if (
      window.UnifiedPerformance &&
      typeof window.UnifiedPerformance.end ===
        'function'
    ) {
      return window.UnifiedPerformance.end(
        tokenId,
        meta || {}
      );
    }

    return null;
  }

  /*
   * ------------------------------------------------------------
   * ERRORS
   * ------------------------------------------------------------
   */

  function reportError(
    severity,
    code,
    message,
    details
  ) {
    if (
      window.UnifiedErrors &&
      typeof window.UnifiedErrors.report ===
        'function'
    ) {
      try {
        return window.UnifiedErrors.report(
          severity,
          code,
          message,
          details || {}
        );
      } catch (error) {
        // Console fallback below.
      }
    }

    const method =
      severity === 'warning'
        ? 'warn'
        : 'error';

    console[method](
      '[Unified Tracking] ' +
        message,
      details || {}
    );

    return null;
  }

  /*
   * ------------------------------------------------------------
   * BUS
   * ------------------------------------------------------------
   */

  function emit(
    type,
    data
  ) {
    if (
      window.UnifiedBus &&
      typeof window.UnifiedBus.emit ===
        'function'
    ) {
      try {
        return window.UnifiedBus.emit(
          type,
          data || {}
        );
      } catch (error) {
        reportError(
          'warning',
          'tracking_bus_emit_failed',
          'Unified Tracking could not emit through Unified Bus.',
          {
            event_type:
              type,

            error:
              error.message
          }
        );
      }
    }

    try {
      window.dispatchEvent(
        new CustomEvent(
          'unified:debug',
          {
            detail: {
              type:
                type,

              timestamp:
                nowIso(),

              data:
                data || {}
            }
          }
        )
      );
    } catch (error) {
      // Best effort only.
    }

    return null;
  }

  /*
   * ------------------------------------------------------------
   * JOURNEY
   * ------------------------------------------------------------
   */

  function recordJourneyEvent(
    eventName,
    payload,
    status
  ) {
    const journey =
      getJourney();

    if (
      !journey ||
      typeof journey.recordEvent !==
        'function'
    ) {
      return null;
    }

    try {
      return journey.recordEvent(
        eventName,
        payload,
        {
          status:
            status
        }
      );
    } catch (error) {
      reportError(
        'warning',
        'journey_record_event_failed',
        'Unified Journey could not record an event.',
        {
          event:
            eventName,

          error:
            error.message
        }
      );

      return null;
    }
  }

  function recordJourneyOutcome(
    eventName,
    status,
    meta
  ) {
    const journey =
      getJourney();

    if (
      !journey ||
      typeof journey.recordOutcome !==
        'function'
    ) {
      return null;
    }

    try {
      return journey.recordOutcome(
        eventName,
        status,
        meta || {}
      );
    } catch (error) {
      reportError(
        'warning',
        'journey_record_outcome_failed',
        'Unified Journey could not record an event outcome.',
        {
          event:
            eventName,

          status:
            status,

          error:
            error.message
        }
      );

      return null;
    }
  }

  /*
   * ------------------------------------------------------------
   * CONSENT GATE
   * ------------------------------------------------------------
   */

  function checkAnalyticsConsent() {
    const config =
      getConfig();

    if (
      config.consentRequired !==
      true
    ) {
      return {
        allowed:
          true,

        reason:
          'consent_not_required'
      };
    }

    const consent =
      getConsent();

    if (!consent) {
      return {
        allowed:
          false,

        reason:
          'consent_manager_unavailable'
      };
    }

    if (
      typeof consent.isReady !==
        'function' ||
      consent.isReady() !==
        true
    ) {
      return {
        allowed:
          false,

        reason:
          'consent_not_ready'
      };
    }

    if (
      typeof consent.analyticsAllowed !==
        'function'
    ) {
      return {
        allowed:
          false,

        reason:
          'analytics_consent_unavailable'
      };
    }

    const allowed =
      consent.analyticsAllowed() ===
      true;

    return {
      allowed:
        allowed,

      reason:
        allowed
          ? 'analytics_consent_allowed'
          : 'analytics_consent_denied'
    };
  }

  /*
   * ------------------------------------------------------------
   * DEPENDENCY CHECK
   * ------------------------------------------------------------
   */

  function getDependencyStatus() {
    return {
      config:
        Boolean(
          window.UnifiedTrackingConfig
        ),

      schema:
        Boolean(
          window.UnifiedSchema
        ),

      normalizer:
        Boolean(
          window.UnifiedNormalizer &&
          typeof window.UnifiedNormalizer
            .normalize ===
            'function'
        ),

      validator:
        Boolean(
          window.UnifiedValidator &&
          typeof window.UnifiedValidator
            .validate ===
            'function'
        ),

      rules:
        Boolean(
          window.UnifiedRulesEngine &&
          typeof window.UnifiedRulesEngine
            .evaluate ===
            'function'
        ),

      bus:
        Boolean(
          window.UnifiedBus &&
          typeof window.UnifiedBus
            .emit ===
            'function'
        ),

      context:
        Boolean(
          window.UnifiedContext &&
          typeof window.UnifiedContext
            .enrich ===
            'function'
        ),

      errors:
        Boolean(
          window.UnifiedErrors
        ),

      journey:
        Boolean(
          window.UnifiedJourney
        ),

      performance:
        Boolean(
          window.UnifiedPerformance
        ),

      consent:
        Boolean(
          window.UnifiedConsent
        ),

      provider_manager:
        Boolean(
          window.UnifiedProviderManager &&
          typeof window.UnifiedProviderManager
            .dispatch ===
            'function'
        )
    };
  }

  function dependenciesReady() {
    const status =
      getDependencyStatus();

    const required = [
      'config',
      'schema',
      'normalizer',
      'validator',
      'rules',
      'bus',
      'context',
      'consent',
      'provider_manager'
    ];

    return required.every(
      function (key) {
        return (
          status[key] ===
          true
        );
      }
    );
  }

  /*
   * ------------------------------------------------------------
   * SAFE FAILURE RESULT
   * ------------------------------------------------------------
   */

  function createFailure(
    requestId,
    eventName,
    stage,
    reason,
    extra
  ) {
    return Object.assign(
      {
        success:
          false,

        request_id:
          requestId,

        event:
          eventName,

        status:
          'blocked',

        stage:
          stage,

        reason:
          reason
      },
      extra || {}
    );
  }

  /*
   * ------------------------------------------------------------
   * MAIN TRACKING PIPELINE
   * ------------------------------------------------------------
   */

  function track(
    eventName,
    payload
  ) {
    const requestId =
      nextEventId();

    const normalizedEventName =
      typeof eventName ===
        'string'
        ? eventName
            .trim()
            .toLowerCase()
        : '';

    const sourcePayload =
      isPlainObject(
        payload
      )
        ? Object.assign(
            {},
            payload
          )
        : {};

    const performanceToken =
      performanceStart(
        'tracking.total',
        {
          event:
            normalizedEventName
        }
      );

    /*
     * ----------------------------------------------------------
     * 0. BASIC INPUT
     * ----------------------------------------------------------
     */

    if (
      !normalizedEventName
    ) {
      const failure =
        createFailure(
          requestId,
          normalizedEventName,
          'input',
          'event_name_invalid'
        );

      reportError(
        'warning',
        'event_name_invalid',
        'Unified Tracking received an invalid event name.',
        {}
      );

      performanceEnd(
        performanceToken,
        {
          event:
            normalizedEventName,

          status:
            'blocked',

          reason:
            failure.reason
        }
      );

      return failure;
    }

    if (
      !isPlainObject(
        payload
      )
    ) {
      const failure =
        createFailure(
          requestId,
          normalizedEventName,
          'input',
          'payload_invalid'
        );

      reportError(
        'warning',
        'payload_invalid',
        'Unified Tracking requires a plain object payload.',
        {
          event:
            normalizedEventName
        }
      );

      performanceEnd(
        performanceToken,
        {
          event:
            normalizedEventName,

          status:
            'blocked',

          reason:
            failure.reason
        }
      );

      return failure;
    }

    /*
     * ----------------------------------------------------------
     * 1. DEPENDENCIES
     * ----------------------------------------------------------
     */

    if (
      !dependenciesReady()
    ) {
      const dependencyStatus =
        getDependencyStatus();

      const failure =
        createFailure(
          requestId,
          normalizedEventName,
          'dependencies',
          'framework_not_ready',
          {
            dependencies:
              dependencyStatus
          }
        );

      reportError(
        'error',
        'framework_not_ready',
        'Unified Tracking cannot process the event because required modules are unavailable.',
        {
          event:
            normalizedEventName,

          missing:
            Object.keys(
              dependencyStatus
            ).filter(
              function (key) {
                return (
                  dependencyStatus[
                    key
                  ] !== true
                );
              }
            )
        }
      );

      performanceEnd(
        performanceToken,
        {
          event:
            normalizedEventName,

          status:
            'blocked',

          reason:
            failure.reason
        }
      );

      return failure;
    }

    /*
     * ----------------------------------------------------------
     * 2. EVENT RECEIVED
     *
     * No payload values are written to Bus.
     * ----------------------------------------------------------
     */

    emit(
      'event_received',
      {
        request_id:
          requestId,

        event:
          normalizedEventName,

        field_names:
          Object.keys(
            sourcePayload
          )
      }
    );

    /*
     * ----------------------------------------------------------
     * 3. CONTEXT ENRICHMENT
     * ----------------------------------------------------------
     */

    const context =
      getContext();

    let enrichedResult;

    try {
      enrichedResult =
        context.enrich(
          normalizedEventName,
          sourcePayload
        );
    } catch (error) {
      const failure =
        createFailure(
          requestId,
          normalizedEventName,
          'context',
          'context_enrichment_failed'
        );

      reportError(
        'error',
        'context_enrichment_failed',
        'Unified Context failed while enriching an event.',
        {
          event:
            normalizedEventName,

          error:
            error.message
        }
      );

      recordJourneyOutcome(
        normalizedEventName,
        'failed',
        {
          reason:
            failure.reason
        }
      );

      performanceEnd(
        performanceToken,
        {
          event:
            normalizedEventName,

          status:
            'error',

          reason:
            failure.reason
        }
      );

      return failure;
    }

    if (
      !enrichedResult ||
      enrichedResult.success !==
        true ||
      !isPlainObject(
        enrichedResult.payload
      )
    ) {
      const failure =
        createFailure(
          requestId,
          normalizedEventName,
          'context',
          'context_enrichment_failed'
        );

      recordJourneyOutcome(
        normalizedEventName,
        'failed',
        {
          reason:
            failure.reason
        }
      );

      performanceEnd(
        performanceToken,
        {
          event:
            normalizedEventName,

          status:
            'error',

          reason:
            failure.reason
        }
      );

      return failure;
    }

    /*
     * ----------------------------------------------------------
     * 4. NORMALIZATION
     * ----------------------------------------------------------
     */

    const normalizer =
      getNormalizer();

    const normalizationResult =
      normalizer.normalize(
        normalizedEventName,
        enrichedResult.payload
      );

    if (
      !normalizationResult ||
      normalizationResult.success !==
        true
    ) {
      const failure =
        createFailure(
          requestId,
          normalizedEventName,
          'normalizer',
          'normalization_failed'
        );

      recordJourneyOutcome(
        normalizedEventName,
        'failed',
        {
          reason:
            failure.reason
        }
      );

      performanceEnd(
        performanceToken,
        {
          event:
            normalizedEventName,

          status:
            'blocked',

          reason:
            failure.reason
        }
      );

      return failure;
    }

    /*
     * ----------------------------------------------------------
     * 5. VALIDATION
     * ----------------------------------------------------------
     */

    const validator =
      getValidator();

    const validationResult =
      validator.validate(
        normalizedEventName,
        normalizationResult.payload
      );

    if (
      !validationResult ||
      validationResult.valid !==
        true
    ) {
      const failure =
        createFailure(
          requestId,
          normalizedEventName,
          'validator',
          'validation_failed',
          {
            errors:
              validationResult &&
              Array.isArray(
                validationResult.errors
              )
                ? validationResult.errors
                    .map(
                      function (error) {
                        return {
                          code:
                            error.code ||
                            '',

                          field:
                            error.field ||
                            ''
                        };
                      }
                    )
                : []
          }
        );

      recordJourneyOutcome(
        normalizedEventName,
        'failed',
        {
          reason:
            failure.reason
        }
      );

      performanceEnd(
        performanceToken,
        {
          event:
            normalizedEventName,

          status:
            'blocked',

          reason:
            failure.reason,

          errors:
            failure.errors.length
        }
      );

      return failure;
    }

    /*
     * ----------------------------------------------------------
     * 6. RULES ENGINE
     * ----------------------------------------------------------
     */

    const rulesEngine =
      getRulesEngine();

    const rulesResult =
      rulesEngine.evaluate(
        normalizedEventName,
        normalizationResult.payload
      );

    if (
      !rulesResult ||
      rulesResult.success !==
        true
    ) {
      const failure =
        createFailure(
          requestId,
          normalizedEventName,
          'rules',
          'rules_evaluation_failed'
        );

      recordJourneyOutcome(
        normalizedEventName,
        'failed',
        {
          reason:
            failure.reason
        }
      );

      performanceEnd(
        performanceToken,
        {
          event:
            normalizedEventName,

          status:
            'error',

          reason:
            failure.reason
        }
      );

      return failure;
    }

    if (
      rulesResult.allowed !==
        true
    ) {
      const failure =
        createFailure(
          requestId,
          normalizedEventName,
          'rules',
          rulesResult.reason ||
            'blocked_by_rules',
          {
            matched_rules:
              rulesResult
                .matched_rules ||
              []
          }
        );

      recordJourneyEvent(
        normalizedEventName,
        rulesResult.payload,
        'blocked'
      );

      recordJourneyOutcome(
        normalizedEventName,
        'blocked',
        {
          reason:
            failure.reason
        }
      );

      performanceEnd(
        performanceToken,
        {
          event:
            normalizedEventName,

          status:
            'blocked',

          reason:
            failure.reason
        }
      );

      return failure;
    }

    /*
     * ----------------------------------------------------------
     * 7. SHOPIFY CONSENT GATE
     *
     * Fail closed.
     *
     * No events are queued while the privacy API
     * is unavailable. This deliberately avoids
     * storing pre-validation/raw interaction data.
     * ----------------------------------------------------------
     */

    const consentResult =
      checkAnalyticsConsent();

    if (
      consentResult.allowed !==
        true
    ) {
      emit(
        'event_consent_blocked',
        {
          request_id:
            requestId,

          event:
            normalizedEventName,

          reason:
            consentResult.reason
        }
      );

      emit(
        'event_blocked',
        {
          request_id:
            requestId,

          event:
            normalizedEventName,

          stage:
            'consent',

          reason:
            consentResult.reason
        }
      );

      recordJourneyEvent(
        normalizedEventName,
        rulesResult.payload,
        'blocked'
      );

      recordJourneyOutcome(
        normalizedEventName,
        'blocked',
        {
          reason:
            consentResult.reason
        }
      );

      performanceEnd(
        performanceToken,
        {
          event:
            normalizedEventName,

          status:
            'blocked',

          reason:
            consentResult.reason
        }
      );

      return createFailure(
        requestId,
        normalizedEventName,
        'consent',
        consentResult.reason
      );
    }

    /*
     * ----------------------------------------------------------
     * 8. EVENT APPROVED
     * ----------------------------------------------------------
     */

    emit(
      'event_consent_approved',
      {
        request_id:
          requestId,

        event:
          normalizedEventName
      }
    );

    emit(
      'event_approved',
      {
        request_id:
          requestId,

        event:
          normalizedEventName,

        service:
          rulesResult
            .payload
            .service,

        event_category:
          rulesResult
            .payload
            .event_category,

        routes:
          rulesResult.routes ||
          []
      }
    );

    recordJourneyEvent(
      normalizedEventName,
      rulesResult.payload,
      'approved'
    );

    recordJourneyOutcome(
      normalizedEventName,
      'approved',
      {
        reason:
          'consent_allowed'
      }
    );

    /*
     * ----------------------------------------------------------
     * 9. PROVIDER DISPATCH
     * ----------------------------------------------------------
     */

    const providerManager =
      getProviderManager();

    const dispatchResult =
      providerManager.dispatch(
        normalizedEventName,
        rulesResult.payload,
        {
          routes:
            rulesResult.routes ||
            []
        }
      );

    if (
      !dispatchResult ||
      dispatchResult.success !==
        true
    ) {
      emit(
        'event_dispatch_failed',
        {
          request_id:
            requestId,

          event:
            normalizedEventName,

          failed:
            dispatchResult &&
            Array.isArray(
              dispatchResult.failed
            )
              ? dispatchResult.failed
              : []
        }
      );

      recordJourneyOutcome(
        normalizedEventName,
        'failed',
        {
          reason:
            'provider_dispatch_failed',

          providers:
            dispatchResult &&
            Array.isArray(
              dispatchResult.failed
            )
              ? dispatchResult.failed
                  .map(
                    function (item) {
                      return (
                        item.provider ||
                        ''
                      );
                    }
                  )
                  .filter(Boolean)
              : []
        }
      );

      performanceEnd(
        performanceToken,
        {
          event:
            normalizedEventName,

          status:
            'error',

          reason:
            'provider_dispatch_failed'
        }
      );

      return createFailure(
        requestId,
        normalizedEventName,
        'provider',
        'provider_dispatch_failed'
      );
    }

    /*
     * ----------------------------------------------------------
     * 10. EVENT DISPATCHED
     * ----------------------------------------------------------
     */

    emit(
      'event_dispatched',
      {
        request_id:
          requestId,

        event:
          normalizedEventName,

        sent:
          dispatchResult.sent ||
          [],

        skipped:
          dispatchResult.skipped ||
          []
      }
    );

    recordJourneyOutcome(
      normalizedEventName,
      'dispatched',
      {
        reason:
          dispatchResult.sent &&
          dispatchResult.sent.length
            ? 'provider_sent'
            : 'no_enabled_provider',

        providers:
          dispatchResult.sent ||
          []
      }
    );

    performanceEnd(
      performanceToken,
      {
        event:
          normalizedEventName,

        status:
          'success'
      }
    );

    const result = {
      success:
        true,

      request_id:
        requestId,

      event:
        normalizedEventName,

      status:
        'dispatched',

      service:
        rulesResult
          .payload
          .service,

      event_category:
        rulesResult
          .payload
          .event_category,

      providers: {
        sent:
          dispatchResult.sent ||
          [],

        skipped:
          dispatchResult.skipped ||
          [],

        failed:
          dispatchResult.failed ||
          []
      },

      fields:
        Object.keys(
          rulesResult.payload
        )
    };

    if (
      isDebugEnabled()
    ) {
      console.info(
        '[Unified Tracking] Event completed',
        {
          requestId:
            requestId,

          event:
            normalizedEventName,

          service:
            result.service,

          sent:
            result.providers.sent,

          skipped:
            result.providers
              .skipped
        }
      );
    }

    return result;
  }

  /*
   * ------------------------------------------------------------
   * STATUS
   * ------------------------------------------------------------
   */

  function getStatus() {
    const consent =
      getConsent();

    const providerManager =
      getProviderManager();

    return {
      version:
        VERSION,

      deployment:
        getConfig().deployment ||
        '',

      environment:
        getConfig().environment ||
        '',

      debug:
        isDebugEnabled(),

      ready:
        dependenciesReady(),

      dependencies:
        getDependencyStatus(),

      consent:
        consent &&
        typeof consent.getState ===
          'function'
          ? consent.getState()
          : null,

      providers:
        providerManager &&
        typeof providerManager.getStatus ===
          'function'
          ? providerManager
              .getStatus()
          : {}
    };
  }

  /*
   * ------------------------------------------------------------
   * PUBLIC API
   * ------------------------------------------------------------
   */

  window.UnifiedTracking = {
    version:
      VERSION,

    track:
      track,

    getStatus:
      getStatus,

    getDependencyStatus:
      getDependencyStatus,

    isReady:
      dependenciesReady
  };

  if (
    isDebugEnabled()
  ) {
    console.info(
      '[Unified Tracking] Orchestrator initialized',
      {
        version:
          VERSION,

        deployment:
          getConfig()
            .deployment ||
          '',

        environment:
          getConfig()
            .environment ||
          '',

        dependencies:
          getDependencyStatus()
      }
    );
  }
})();